import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import axios from 'axios';
import { toast } from 'sonner';
import '../../styles/leaflet-custom.css';
import '../../styles/map-advanced.css';
import { MapPin, Search, Filter, X, BarChart3 } from 'lucide-react';
import MapControls from './MapControls';
import AdvancedSearch from './AdvancedSearch';
import AdvancedStats from './AdvancedStats';
import HeatmapLayer from './HeatmapLayer';
import ThemeToggle from './ThemeToggle';
import useMapWebSocket from '../../hooks/useMapWebSocket';

import { icons, AutoCenter, TERRITOIRE_PRESETS } from './mapHelpers';
import useMapWorld from './useMapWorld';
import MapOverlays from './MapOverlays';

const MapWorld = (props) => {
  const ctx = useMapWorld(props);
  const {
    activeTerritoire, autoRefresh, fetchMapData, filteredData, filters, focusTerritoire,
    geoCounts, getAuthHeader, handleAdvancedSearch, handleSearch, heatmapEnabled, heatmapPoints,
    isAdminView, isConnected, lastUpdate, loading, mapCenter, mapData,
    mapZoom, refreshInterval, searchFilters, searchResults, searchTerm, setActiveTerritoire,
    setAutoRefresh, setFilters, setHeatmapEnabled, setLoading, setMapCenter, setMapData,
    setMapZoom, setRefreshInterval, setSearchFilters, setSearchTerm, setShowAdvancedSearch, setShowAdvancedStats,
    setShowFilters, setTheme, setTimelineFilter, showAdvancedSearch, showAdvancedStats, showFilters,
    stats, theme, timelineFilter, toggleFilter, visibleMarkerCount,
  } = ctx;

  if (loading) {
    return (
      <div className="map-loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Chargement de la carte...</div>
      </div>
    );
  }

  return (
    <div className={`map-container ${theme}-theme`}>
      <MapOverlays {...ctx} />

      {/* Carte Leaflet */}
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ width: '100%', height: '100%' }}
        className="map-wrapper"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <AutoCenter center={mapCenter} zoom={mapZoom} />
        
        <MarkerClusterGroup>
          {/* Marqueurs Chantiers RCDS */}
          {filteredData.chantiers_rcd.map((chantier) => (
            chantier.geo_location && (
              <Marker
                key={chantier.id}
                position={[chantier.geo_location.latitude, chantier.geo_location.longitude]}
                icon={icons.chantier}
              >
                <Popup>
                  <div className="popup-header">🏗️ {chantier.nom_chantier}</div>
                  <div className="popup-row">
                    <span className="popup-label">Type RCDS:</span>
                    <span className="popup-value">{chantier.type_rcd}</span>
                  </div>
                  <div className="popup-row">
                    <span className="popup-label">Statut:</span>
                    <span className="popup-badge badge-info">{chantier.status}</span>
                  </div>
                  <div className="popup-row">
                    <span className="popup-label">Montant:</span>
                    <span className="popup-value">{chantier.montant_travaux.toLocaleString()}€</span>
                  </div>
                  <div className="popup-row">
                    <span className="popup-label">Maître d'ouvrage:</span>
                    <span className="popup-value">{chantier.maitre_ouvrage_nom}</span>
                  </div>
                  <div className="popup-row">
                    <span className="popup-label">Avancement:</span>
                    <span className="popup-value">{chantier.avancement_pct}%</span>
                  </div>
                </Popup>
              </Marker>
            )
          ))}
          
          {/* Marqueurs Membres */}
          {filteredData.membres.map((membre) => (
            membre.geo_location && (
              <Marker
                key={membre.id}
                position={[membre.geo_location.latitude, membre.geo_location.longitude]}
                icon={icons.membre}
              >
                <Popup>
                  <div className="popup-header">👤 {membre.nom}</div>
                  <div className="popup-row">
                    <span className="popup-label">Email:</span>
                    <span className="popup-value">{membre.email}</span>
                  </div>
                  <div className="popup-row">
                    <span className="popup-label">Statut:</span>
                    <span className="popup-badge badge-success">{membre.statut}</span>
                  </div>
                  <div className="popup-row">
                    <span className="popup-label">Dispositifs:</span>
                    <span className="popup-value">{membre.nombre_dispositifs}</span>
                  </div>
                  {membre.dispositifs_actifs.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div className="popup-label">Dispositifs actifs:</div>
                      {membre.dispositifs_actifs.map((disp, idx) => (
                        <span key={idx} className="popup-badge badge-info" style={{ marginRight: 4, marginTop: 4 }}>
                          {disp}
                        </span>
                      ))}
                    </div>
                  )}
                  {membre.cooper_nom && (
                    <div className="popup-row">
                      <span className="popup-label">COOPER:</span>
                      <span className="popup-value">{membre.cooper_nom}</span>
                    </div>
                  )}
                </Popup>
              </Marker>
            )
          ))}
          
          {/* Marqueurs Dommages */}
          {filteredData.dommages.map((dommage) => (
            dommage.geo_location && (
              <Marker
                key={dommage.id}
                position={[dommage.geo_location.latitude, dommage.geo_location.longitude]}
                icon={icons.dommage}
              >
                <Popup>
                  <div className="popup-header">⚠️ {dommage.titre}</div>
                  <div className="popup-row">
                    <span className="popup-label">Type:</span>
                    <span className="popup-value">{dommage.type_dommage}</span>
                  </div>
                  <div className="popup-row">
                    <span className="popup-label">Statut:</span>
                    <span className="popup-badge badge-warning">{dommage.statut}</span>
                  </div>
                  <div className="popup-row">
                    <span className="popup-label">Maître d'ouvrage:</span>
                    <span className="popup-value">{dommage.maitre_ouvrage_nom}</span>
                  </div>
                  {dommage.montant_estime && (
                    <div className="popup-row">
                      <span className="popup-label">Montant estimé:</span>
                      <span className="popup-value">{dommage.montant_estime.toLocaleString()}€</span>
                    </div>
                  )}
                  {dommage.expert_nom && (
                    <div className="popup-row">
                      <span className="popup-label">Expert:</span>
                      <span className="popup-value">{dommage.expert_nom}</span>
                    </div>
                  )}
                  <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                    {dommage.description}
                  </div>
                </Popup>
              </Marker>
            )
          ))}
          
          {/* Marqueurs Travaux Réparation */}
          {filteredData.travaux_reparation.map((travaux) => (
            travaux.geo_location && (
              <Marker
                key={travaux.id}
                position={[travaux.geo_location.latitude, travaux.geo_location.longitude]}
                icon={icons.travaux}
              >
                <Popup>
                  <div className="popup-header">🔧 {travaux.dommage_titre}</div>
                  <div className="popup-row">
                    <span className="popup-label">Statut:</span>
                    <span className="popup-badge badge-info">{travaux.status}</span>
                  </div>
                  <div className="popup-row">
                    <span className="popup-label">Avancement:</span>
                    <span className="popup-value">{travaux.avancement_pct}%</span>
                  </div>
                  <div className="popup-row">
                    <span className="popup-label">Montant devis:</span>
                    <span className="popup-value">{travaux.montant_devis.toLocaleString()}€</span>
                  </div>
                  {travaux.expert_noms.length > 0 && (
                    <div className="popup-row">
                      <span className="popup-label">Experts:</span>
                      <span className="popup-value">{travaux.expert_noms.join(', ')}</span>
                    </div>
                  )}
                  {travaux.entreprise_noms.length > 0 && (
                    <div className="popup-row">
                      <span className="popup-label">Entreprises:</span>
                      <span className="popup-value">{travaux.entreprise_noms.join(', ')}</span>
                    </div>
                  )}
                </Popup>
              </Marker>
            )
          ))}
          
          {/* Marqueurs COOPER's */}
          {filteredData.coopers.map((cooper) => (
            cooper.geo_location && (
              <Marker
                key={cooper.id}
                position={[cooper.geo_location.latitude, cooper.geo_location.longitude]}
                icon={icons.cooper}
              >
                <Popup>
                  <div className="popup-header">🤝 {cooper.nom}</div>
                  {isAdminView && (
                    <div className="popup-row">
                      <span className="popup-label">Email:</span>
                      <span className="popup-value">{cooper.email}</span>
                    </div>
                  )}
                  <div className="popup-row">
                    <span className="popup-label">Territoire:</span>
                    <span className="popup-value">{cooper.territoire}</span>
                  </div>
                  {isAdminView && (
                    <>
                      <div className="popup-row">
                        <span className="popup-label">Membres associés:</span>
                        <span className="popup-value">{cooper.nombre_membres}</span>
                      </div>
                      <div className="popup-row">
                        <span className="popup-label">Dossiers traités:</span>
                        <span className="popup-value">{cooper.dossiers_traites}</span>
                      </div>
                    </>
                  )}
                  <div className="popup-row">
                    <span className="popup-label">Statut:</span>
                    <span className="popup-badge badge-success">{cooper.statut}</span>
                  </div>
                </Popup>
              </Marker>
            )
          ))}
        </MarkerClusterGroup>
        
        {/* Heatmap Layer */}
        {heatmapEnabled && <HeatmapLayer points={heatmapPoints} />}
      </MapContainer>
      
      {/* Toggle Dark/Light */}
      <ThemeToggle 
        theme={theme} 
        onToggle={() => setTheme(prev => prev === 'dark' ? 'light' : 'dark')}
      />
      
      {/* Contrôles Avancés */}
      {isAdminView && (
        <MapControls
          mapData={mapData}
          onTimelineChange={(date) => {
            setTimelineFilter(date);
          }}
          onHeatmapToggle={(enabled) => {
            setHeatmapEnabled(enabled);
          }}
        />
      )}
      
      {/* Bouton Stats Avancées */}
      {isAdminView && !showAdvancedStats && (
        <button
          onClick={() => setShowAdvancedStats(true)}
          className="stats-toggle-btn"
          title="Statistiques avancées"
        >
          <BarChart3 className="h-6 w-6" />
        </button>
      )}
      
      {/* Panneau Stats Avancées */}
      {isAdminView && showAdvancedStats && (
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowAdvancedStats(false)}
            style={{
              position: 'absolute',
              top: 20,
              right: 430,
              zIndex: 1001,
              background: 'rgba(255, 0, 0, 0.2)',
              border: '1px solid rgba(255, 0, 0, 0.4)',
              borderRadius: '12px',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#ff4444'
            }}
          >
            <X className="h-5 w-5" />
          </button>
          <AdvancedStats mapData={filteredData} />
        </div>
      )}
      
      {/* Recherche Avancée Modal */}
      {showAdvancedSearch && (
        <AdvancedSearch
          onSearch={handleAdvancedSearch}
          onClose={() => setShowAdvancedSearch(false)}
        />
      )}
      
      {/* Indicateur WebSocket */}
      {isAdminView && (
        <div className="websocket-indicator" style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 999,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          background: isConnected 
            ? 'rgba(16, 185, 129, 0.2)' 
            : 'rgba(239, 68, 68, 0.2)',
          border: `1px solid ${isConnected ? '#10b981' : '#ef4444'}`,
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 600,
          color: isConnected ? '#10b981' : '#ef4444'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: isConnected ? '#10b981' : '#ef4444',
            boxShadow: `0 0 10px ${isConnected ? '#10b981' : '#ef4444'}`,
            animation: isConnected ? 'pulse 2s ease-in-out infinite' : 'none'
          }} />
          {isConnected ? 'Temps Réel Actif' : 'Déconnecté'}
        </div>
      )}
    </div>
  );
};

export default MapWorld;
